import { Action, Computed, Thunk, action, computed, thunk } from 'easy-peasy'
import { BankrateLoanTerms, MortgageRateTableDimensions } from '@bankrate/mortgage-types'
import { populateFromQueryParams, updateQueryParams } from '../../utils/queryParams'
import merge from 'lodash/merge'
import get from 'lodash/get'
import set from 'lodash/set'
import debounce from 'lodash/debounce'
import { StoreModel } from 'store'
import { rollbar } from 'utils/rollbar'
import currency from 'utils/currency'
import { scrollToTableTop } from 'utils/scroll'

type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>
}

type DisplayTarget =
  | 'desktopRateTable'
  | 'mobileRateTable'
  | 'featuredRateTable'
  | 'desktopSecondaryRT'
  | 'mobileSecondaryRT'
  | 'featuredSecondaryRT'
  | 'desktopPaidRT'
  | 'mobilePaidRT'
  | 'featuredPaidRT'

export type DisplayTab = 'all' | 'featured'

export interface ValidationSet {
  invalid: boolean
  errorMsg: string
  postLoadCheck?: boolean
}
interface DimensionsValidationType {
  zipCode: ValidationSet
  purchase: {
    price: ValidationSet
    downPayment: ValidationSet
    minLtvRatio: ValidationSet
    points: ValidationSet
  }
  refinance: {
    loanAmount: ValidationSet
    minLtvRatio: ValidationSet
    maxLtvRatio: ValidationSet
    loanAmountHigh: ValidationSet
    cashOutAmount: ValidationSet
    propertyValue: ValidationSet
    points: ValidationSet
  }
}

export interface DimensionsModel {
  // State
  dimensions: MortgageRateTableDimensions
  stickyValue: 'downPaymentPercentage' | 'downPayment'
  displayTargets: DisplayTarget[]
  activeDisplayTab: DisplayTab
  deviceType: 'desktop' | 'mobile'
  tabletDevice: boolean
  downPaymentPercentage: number
  dimensionsRecentlyChanged: boolean
  fhaChanged: boolean

  // computed state
  dimensionsValidation: Computed<DimensionsModel, DimensionsValidationType>
  hasInvalidDimension: Computed<DimensionsModel, boolean>
  hasPostLoadInvalidDimension: Computed<DimensionsModel, boolean>
  calculatedLoanAmount: Computed<DimensionsModel, number>
  isDesktop: Computed<DimensionsModel, boolean>
  isMobilePhoneDevice: Computed<DimensionsModel, boolean>

  // Actions
  setDimensions: Action<DimensionsModel, RecursivePartial<MortgageRateTableDimensions>>
  setStickyValue: Action<DimensionsModel, 'downPaymentPercentage' | 'downPayment'>
  setLoanTerms: Action<DimensionsModel, BankrateLoanTerms[]>
  setDisplayTargets: Action<DimensionsModel, DisplayTarget[]>
  setActiveDisplayTab: Action<DimensionsModel, DisplayTab>
  setDeviceType: Action<DimensionsModel, 'desktop' | 'mobile'>
  setTabletDeviceType: Action<DimensionsModel, boolean>
  setDownPaymentPercentage: Action<DimensionsModel, number>
  setDimensionsRecentlyChanged: Action<DimensionsModel, boolean>
  setFhaChanged: Action<DimensionsModel, boolean>

  // Thunks (AKA Mutations)
  updateDimensions: Thunk<DimensionsModel, RecursivePartial<MortgageRateTableDimensions>, any, StoreModel>
  updateLoanTerms: Thunk<DimensionsModel, RecursivePartial<BankrateLoanTerms[]>>
  updateDownPaymentPercentage: Thunk<DimensionsModel, number>

  updatePrice: Thunk<DimensionsModel, number, unknown>
  updateDownPayment: Thunk<DimensionsModel, number, unknown>
}

const debouncedSearch = debounce((getStoreActions: any, getStoreState: any) => {
  const dimensionsState = getStoreState().dimensions as DimensionsModel
  getStoreActions().offers.getOffers(dimensionsState.dimensions)

  if (dimensionsState.deviceType === 'mobile') {
    scrollToTableTop()
  }
}, 500)

export const stateToQueryParamsMap = new Map([
  ['pointsChanged', 'pointsChanged'],
  ['searchChanged', 'searchChanged'],
  ['mortgageType', 'mortgageType'],
  ['partnerId', 'partnerId'],
  ['zipCode', 'zipCode'],
  ['user.creditScore', 'userCreditScore'],
  ['user.debtToIncomeRatio', 'userDebtToIncomeRatio'],
  ['user.fha', 'userFha'],
  ['user.veteranStatus', 'userVeteranStatus'],
  ['purchase.price', 'purchasePrice'],
  ['purchase.downPayment', 'purchaseDownPayment'],
  ['purchase.propertyType', 'purchasePropertyType'],
  ['purchase.propertyUse', 'purchasePropertyUse'],
  ['purchase.points', 'purchasePoints'],
  ['purchase.loanTerms', 'purchaseLoanTerms'],
  ['refinance.loanAmount', 'refinanceLoanAmount'],
  ['refinance.propertyValue', 'refinancePropertyValue'],
  ['refinance.cashOutAmount', 'refinanceCashOutAmount'],
  ['refinance.propertyType', 'refinancePropertyType'],
  ['refinance.propertyUse', 'refinancePropertyUse'],
  ['refinance.points', 'refinancePoints'],
  ['refinance.loanTerms', 'refinanceLoanTerms'],
  ['ttcid', 'ttcid'],
  ['pid', 'pid'],
  ['showingStacked', 'showingStacked'],
  ['targetRuleId', 'targetRuleId'],
])

export const defaultDebtToIncomeRatio = 0

export const defaultDimensions: MortgageRateTableDimensions = {
  pointsChanged: false,
  searchChanged: false,
  mortgageType: 'Purchase',
  partnerId: 'br3',
  zipCode: '90210',
  ttcid: undefined,
  pid: 'br3',
  user: {
    creditScore: 780,
    debtToIncomeRatio: defaultDebtToIncomeRatio,
    veteranStatus: 'NoMilitaryService',
    hadPriorVaLoan: false,
    hasVaDisabilities: false,
    firstTimeHomebuyer: false,
    quickClosing: false,
    fha: false,
    lowUpfrontCosts: false,
    lowPayment: false,
  },
  purchase: {
    loanTerms: ['30yr'] as BankrateLoanTerms[],
    price: 400000,
    downPayment: 80000,
    propertyType: 'SingleFamily',
    propertyUse: 'PrimaryResidence',
    points: 'All',
  },
  refinance: {
    loanTerms: ['30yr'] as BankrateLoanTerms[],
    loanAmount: 320000,
    propertyValue: 400000,
    cashOutAmount: 0,
    propertyType: 'SingleFamily',
    propertyUse: 'PrimaryResidence',
    points: 'All',
  },
}

const dimensions: DimensionsModel = {
  // State initial values
  dimensions: populateFromQueryParams<MortgageRateTableDimensions>(
    // merge default dimensions with any "passed in" overrides from fantasia globalThis object
    merge(defaultDimensions, updateInitialDimensions(globalThis.initialDimensions)),
    stateToQueryParamsMap
  ),
  stickyValue: 'downPaymentPercentage',
  displayTargets: ['featuredRateTable', 'desktopRateTable'],
  activeDisplayTab: 'all',
  deviceType: 'desktop',
  tabletDevice: false,
  downPaymentPercentage: 20,
  dimensionsRecentlyChanged: false,
  fhaChanged: false,

  // computed state
  dimensionsValidation: computed(
    [
      state => state.dimensions,
      // @ts-ignore
      (state, storeState) => storeState.location.location,
    ],
    (dimensions, location) => {
      const purchase = dimensions.purchase
      const refinance = dimensions.refinance
      const maxLoanAmount = 1.25 * refinance.propertyValue

      return {
        /*
         * Note: this validation is only for ui validation
         * see queryParams.ts for additional checks if someone tries to override in the url
         */
        zipCode: {
          invalid: location.type === 'Invalid',
          errorMsg: 'Enter a valid ZIP code.',
        },
        purchase: {
          price: {
            invalid: purchase.price < 50000,
            errorMsg: 'Minimum of $50,000 required.',
          },
          downPayment: {
            invalid: purchase.downPayment > purchase.price - 10000,
            errorMsg: 'Must be at least $10,000 less than purchase price.',
          },
          minLtvRatio: {
            invalid: (purchase.price - purchase.downPayment) / purchase.price < 0.01, // Loan payment <1%
            errorMsg: 'Down payment needs to be < 99% of purchase price',
          },
          points: {
            invalid: !['All', 'Zero', 'ZeroToOneInclusive', 'OneToTwo'].includes(purchase.points),
            errorMsg: 'Points can only be All, Zero, ZeroToOneInclusive, or OneToTwo',
          },
        },
        refinance: {
          loanAmount: {
            invalid: refinance.loanAmount < 10000,
            errorMsg: 'Minimum of $10,000 required',
          },
          loanAmountHigh: {
            invalid: !refinance.cashOutAmount && refinance.loanAmount > refinance.propertyValue,
            errorMsg: 'Your loan balance is greater than the value of your property',
          },
          minLtvRatio: {
            invalid: refinance.loanAmount / refinance.propertyValue < 0.01, // down payment <1%
            errorMsg: 'Loan payment <1% of property value ',
          },
          maxLtvRatio: {
            invalid: !refinance.cashOutAmount && refinance.loanAmount / refinance.propertyValue > 0.8,
            errorMsg: `Loan balance exceeds 80% loan-to-value ratio, try lowering to $${currency.formatNumeral(
              refinance.propertyValue * 0.8 - 1,
              0
            )}.`,
            postLoadCheck: true,
          },
          cashOutAmount: {
            invalid: !!refinance.cashOutAmount && refinance.cashOutAmount + refinance.loanAmount > maxLoanAmount,
            errorMsg: 'Cash out + loan is > 125% of property value.',
          },
          propertyValue: {
            invalid: refinance.propertyValue < 50000,
            errorMsg: 'Minimum of $50,000 required.',
          },
          points: {
            invalid: !['All', 'Zero', 'ZeroToOneInclusive', 'OneToTwo'].includes(refinance.points),
            errorMsg: 'Points can only be All, Zero, ZeroToOneInclusive, or OneToTwo',
          },
        },
      }
    }
  ),

  hasInvalidDimension: computed(state => {
    try {
      const mortgageType = state.dimensions.mortgageType.toLowerCase()
      return Object.values<ValidationSet>({
        ...(state.dimensionsValidation as any)[mortgageType],
        zipCode: state.dimensionsValidation.zipCode,
      }).some((rule: ValidationSet) => rule.invalid && !rule.postLoadCheck)
    } catch (err: any) {
      rollbar.error('hasInvalidDimension computed error', ...err)
      return false
    }
  }),

  hasPostLoadInvalidDimension: computed(state => {
    try {
      const mortgageType = state.dimensions.mortgageType.toLowerCase()
      return Object.values<ValidationSet>({
        ...(state.dimensionsValidation as any)[mortgageType],
        zipCode: state.dimensionsValidation.zipCode,
      }).some((rule: ValidationSet) => rule.invalid && rule.postLoadCheck)
    } catch (err: any) {
      rollbar.error('hasPostLoadInvalidDimension computed error', ...err)
      return false
    }
  }),

  calculatedLoanAmount: computed(state => {
    const refiLoanAmount = state.dimensions.refinance.loanAmount
    const purLoanAmount = state.dimensions.purchase.price - state.dimensions.purchase.downPayment
    return state.dimensions.mortgageType === 'Purchase' ? purLoanAmount : refiLoanAmount
  }),

  isDesktop: computed(state => {
    return (
      state.displayTargets.includes('desktopRateTable') ||
      state.displayTargets.includes('desktopSecondaryRT') ||
      state.displayTargets.includes('desktopPaidRT')
    )
  }),

  isMobilePhoneDevice: computed(state => {
    return state.deviceType === 'mobile' && !state.tabletDevice
  }),

  // action definitions
  setDimensions: action((state, payload) => {
    state.dimensions = merge(state.dimensions, payload)
    updateQueryParams(state.dimensions, stateToQueryParamsMap)
  }),

  setStickyValue: action((state, payload) => {
    state.stickyValue = payload
  }),

  setLoanTerms: action((state, payload: BankrateLoanTerms[]) => {
    let currentTerms =
      state.dimensions.mortgageType === 'Purchase'
        ? state.dimensions.purchase.loanTerms
        : state.dimensions.refinance.loanTerms

    payload.forEach(term => {
      if (currentTerms.includes(term)) {
        // Remove the term
        currentTerms = currentTerms.filter(e => e !== term)
      } else {
        // Add the term
        currentTerms.push(term)
      }
    })

    state.dimensions.purchase.loanTerms = currentTerms
    state.dimensions.refinance.loanTerms = currentTerms

    updateQueryParams(state.dimensions, stateToQueryParamsMap)
  }),

  setDisplayTargets: action((state, payload) => {
    state.displayTargets = payload
  }),

  setActiveDisplayTab: action((state, display) => {
    state.activeDisplayTab = display
  }),

  setDeviceType: action((state, device) => {
    state.deviceType = device
  }),

  setTabletDeviceType: action((state, isTablet) => {
    state.tabletDevice = isTablet
  }),

  setDownPaymentPercentage: action((state, downPaymentPercentage) => {
    state.downPaymentPercentage = downPaymentPercentage
  }),

  setDimensionsRecentlyChanged: action((state, recentlyChanged) => {
    state.dimensionsRecentlyChanged = recentlyChanged
  }),

  setFhaChanged: action((state, fhaChanged) => {
    state.fhaChanged = fhaChanged
  }),

  // thunk definitions
  updateDimensions: thunk((actions, payload, { getState, getStoreActions, getStoreState }) => {
    actions.setDimensions(payload)

    if (getStoreState().featureFlags.flags.includeFhaAutomatically) {
      // Business logic: if downPayment is <20, set FHA to true
      // but allow user to override through click
      if (getState().dimensions.mortgageType === 'Purchase') {
        const downPaymentPercentage = getDownPaymentPercentage(
          getState().dimensions.purchase.downPayment,
          getState().dimensions.purchase.price
        )
        if (downPaymentPercentage < 20 && payload?.user?.fha !== false) {
          actions.setDimensions({ user: { fha: true } })
        }
      } else {
        const LTV = getDownPaymentPercentage(
          getState().calculatedLoanAmount,
          getState().dimensions.refinance.propertyValue
        )
        if (LTV > 80 && payload?.user?.fha !== false) {
          actions.setDimensions({ user: { fha: true } })
        }
      }
    }

    if (
      [600, 630].includes(getState().dimensions.user.creditScore) &&
      payload?.user?.fha !== false &&
      getStoreState().dimensions.fhaChanged === false
    ) {
      actions.setDimensions({ user: { fha: true } })
      actions.setFhaChanged(true)
    }

    actions.setDimensionsRecentlyChanged(true)
    debouncedSearch(getStoreActions, getStoreState)
  }),

  updateLoanTerms: thunk(
    (actions, payload: RecursivePartial<BankrateLoanTerms>[], { getStoreActions, getStoreState }) => {
      actions.setLoanTerms(payload)

      actions.setDimensionsRecentlyChanged(true)
      debouncedSearch(getStoreActions, getStoreState)
    }
  ),

  updatePrice: thunk((actions, price: number, helpers) => {
    if (helpers.getState().stickyValue === 'downPaymentPercentage') {
      const downPaymentPercentage = helpers.getState().downPaymentPercentage
      const downPayment = Math.round(price * (downPaymentPercentage / 100))
      actions.updateDimensions({
        purchase: { price, downPayment },
      })
    } else {
      const downPayment = helpers.getState().dimensions.purchase.downPayment
      actions.updateDimensions({
        purchase: { price },
      })
      actions.setDownPaymentPercentage(getDownPaymentPercentage(downPayment, price))
    }
  }),

  updateDownPayment: thunk((actions, downPayment, helpers) => {
    const price = helpers.getState().dimensions.purchase.price
    actions.updateDimensions({ purchase: { downPayment } })
    actions.setDownPaymentPercentage(getDownPaymentPercentage(downPayment, price))
  }),

  updateDownPaymentPercentage: thunk((actions, downPaymentPercentage, helpers) => {
    const price = helpers.getState().dimensions.purchase.price
    const downPayment = Math.round(price * (downPaymentPercentage / 100))
    actions.updateDimensions({
      purchase: { price, downPayment },
    })
    actions.setDownPaymentPercentage(downPaymentPercentage)
  }),
}

export default dimensions

export function getDownPaymentPercentage(downPayment: number, price: number): number {
  if (price === 0) {
    return 0
  }
  const percentage = (downPayment / price) * 100
  return Number(percentage.toFixed(1))
}

export function updateInitialDimensions(initialDimensions: MortgageRateTableDimensions): MortgageRateTableDimensions {
  const averagePurchasePrice = 489450
  const averagePurchaseLtV = 0.8
  const averageRefiPropertValue = 489450
  const averageRefiLoanBalance = 0.6

  if (!get(initialDimensions, 'purchase.price')) {
    set(initialDimensions, 'purchase.price', averagePurchasePrice)
  }

  if (!get(initialDimensions, 'purchase.downPayment')) {
    set(initialDimensions, 'purchase.downPayment', averagePurchasePrice * averagePurchaseLtV)
  }

  if (!get(initialDimensions, 'refinance.propertyValue')) {
    set(initialDimensions, 'refinance.propertyValue', averageRefiPropertValue)
  }

  if (!get(initialDimensions, 'refinance.loanAmount')) {
    set(initialDimensions, 'refinance.loanAmount', averageRefiPropertValue * averageRefiLoanBalance)
  }

  return initialDimensions
}
